/**
 * The top-right Menu affordance's geometry — split out of layout-engine.js
 * (menu-on-start-screen-and-sizing) so the start screen can size its own Menu-trigger
 * instance (`.menu-button-start`, game-start/game-start-ui.js) without importing the
 * board solver: layout-engine.js pulls in layout-solver.js + bidding-constellation.js
 * (691 lines of board-only geometry), and game-start-layout.js deliberately never calls
 * computeLayout (no game state, no oval — see that file's header) — pulling the solver
 * graph onto the start screen's boot payload would undo the lazy-game-graph-boot-seam
 * work. Import ONLY constants.js so this stays eager-safe.
 *
 * `applyMenuAffordanceVars` is the SINGLE writer of the four `--menu-*` vars, called
 * from both layout paths (board-layout.js#applyLayoutProperties,
 * game-start-layout.js#placeStartAssembly) off the same container width/height — one
 * writer, because two drifting writers is exactly the bug this module fixes: the
 * start screen's button silently rendered at 2×2px (the vars were simply never
 * published there) while the in-game button rendered correctly.
 */
import { MENU } from "../constants.js";

/**
 * The top-right Menu affordance's size, icon size, and corner inset.
 * Engine-authored, CSS-consumed: `.menu-button`/`.menu-button-start` read
 * `--menu-size`, `--menu-icon-size`, `--menu-inset`. Pure function. Scales with the
 * smaller viewport dimension (the scarce axis on landscape phones), clamped at both ends.
 */
export function computeMenuAffordance(width, height) {
  const minDim = Math.min(width, height);
  const size = Math.max(
    MENU.MIN_SIZE,
    Math.min(minDim * MENU.SIZE_RATIO, MENU.MAX_SIZE),
  );
  const inset = Math.max(
    MENU.MIN_INSET,
    Math.min(minDim * MENU.INSET_RATIO, MENU.MAX_INSET),
  );
  return { size, iconSize: size * MENU.ICON_RATIO, inset };
}

/** Write the `--menu-*` vars onto `appEl.style` from the container's own width/height.
 *  The single writer both layout paths call.
 *
 *  `--menu-gutter` is the width the page reserves for its scrollbar (0 where nothing is
 *  reserved — in-game always, and anywhere the browser draws overlay scrollbars): the start
 *  screen holds a stable gutter (`html.game-start-open`, skat.css) because its leaderboard
 *  and footer land async and would otherwise shift the scene mid-entrance, and that gutter
 *  narrows #skat-app, so a button inset from the app's right edge sits a scrollbar's width
 *  left of where the in-game instance mounts on the full-width board — it visibly hopped at
 *  the Play swap (measured 15px at 1440×900, menu-strip-play-exit-flight). Both instances
 *  subtract it from their inset — they share one folded rule in skat.css — which pins them to
 *  the same SCREEN position without reserving anything in-game, because in-game the value is
 *  simply 0. Written from both paths, and `clearAppForRender` wipes #skat-app's inline style
 *  and re-applies the layout properties before `renderMenuButton` mounts the in-game instance,
 *  so no stale start-screen gutter can survive into the board's first paint. */
export function applyMenuAffordanceVars(appEl, width, height) {
  const menu = computeMenuAffordance(width, height);
  const s = appEl.style;
  s.setProperty("--menu-size", `${menu.size}px`);
  s.setProperty("--menu-icon-size", `${menu.iconSize}px`);
  s.setProperty("--menu-inset", `${menu.inset}px`);
  // The gutter is what the LAYOUT viewport has that the html box doesn't: `clientWidth`
  // reports the full layout viewport beside a reserved scrollbar gutter (1440 at 1440×900)
  // while the box itself is narrower (1425), and that gap is the gutter. Both terms have to
  // come from the document — `window.innerWidth` is the VISUAL viewport, which diverges under
  // pinch zoom and under mobile emulation (a 390px layout viewport reporting 469), and reading
  // it here once put the gutter at ~79px and shoved the button off the page's right edge.
  const root = document.documentElement;
  const gutter =
    root.clientWidth - Math.round(root.getBoundingClientRect().width);
  s.setProperty("--menu-gutter", `${gutter}px`);
}
